import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import axios from 'axios';
import {
    Megaphone, Plus, Pencil, Trash2, Check, X, Search, ChevronDown,
    Loader2, Pin, Bell, Eye, Image as ImageIcon,
    Upload, Smartphone, Calendar,
    User, AlertTriangle, AlertCircle
} from 'lucide-react';
import { API_URL } from '../config';
import Navbar from './Navbar';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import th from 'date-fns/locale/th';

registerLocale('th', th);

const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch (e) {
        return '';
    }
};

const formatThaiBuddhistDate = (dateVal) => {
    if (!dateVal) return '';
    try {
        const date = new Date(dateVal);
        if (isNaN(date.getTime())) return '';
        const day = date.getDate();
        const months = [
            'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
            'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
        ];
        const month = months[date.getMonth()];
        const yearBE = (date.getFullYear() + 543) % 100;
        return `${day} ${month} ${yearBE}`;
    } catch (e) {
        return '';
    }
};

const DateInputButton = forwardRef(({ value, onClick, placeholder, icon: Icon, onClear, displayValue }, ref) => {
    const hasValue = displayValue || value;
    return (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            className="w-full bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 h-9 gap-2 text-left focus:outline-none hover:border-slate-300 transition-colors"
        >
            {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <span className={`text-[13px] font-medium truncate flex-1 ${hasValue ? 'text-slate-700' : 'text-slate-400'}`}>
                {displayValue || value || placeholder}
            </span>
            {hasValue && onClear ? (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                    }}
                    className="w-4 h-4 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-650 hover:bg-slate-100 transition-colors cursor-pointer shrink-0 z-10"
                >
                    <X className="w-2.5 h-2.5" />
                </span>
            ) : (
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            )}
        </button>
    );
});
DateInputButton.displayName = 'DateInputButton';

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="relative w-full md:w-auto" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full md:w-auto bg-slate-100 hover:bg-slate-200/60 px-4 py-2 h-9 text-xs font-semibold text-slate-700 rounded-lg flex items-center justify-between gap-2.5 cursor-pointer border-0 focus:outline-none transition-all"
            >
                <span className="truncate">{label}: {selectedOption?.label || allLabel}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-250 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-[60] mt-1 w-full right-0 bg-white border border-slate-200/80 rounded-xl shadow-lg py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top-right">
                    {options.map((opt, idx) => (
                        <button
                            key={idx}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                            className={`w-full text-left px-3.5 py-1.5 text-[11px] font-semibold flex items-center justify-between transition-colors cursor-pointer border-none bg-transparent ${value === opt.value ? 'bg-slate-50 text-blue-600 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                        >
                            <span className="truncate">{opt.label}</span>
                            {value === opt.value && <Check className="w-3 h-3 text-blue-600 shrink-0" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


const getScheduleStatus = (ann) => {
    const isActive = ann.is_active === 1 || ann.is_active === true;
    if (!isActive) return { label: 'ปิด/แบบร่าง', style: 'bg-slate-500/90 backdrop-blur-md border border-white/20 text-white' };

    const now = Date.now();
    if (ann.start_date) {
        const d = new Date(ann.start_date);
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
        if (now < start) {
            return { label: 'ตั้งเวลา', style: 'bg-blue-500/90 backdrop-blur-md border border-white/20 text-white' };
        }
    }
    if (ann.end_date) {
        const d = new Date(ann.end_date);
        const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
        if (now > end) {
            return { label: 'หมดอายุ', style: 'bg-rose-500/90 backdrop-blur-md border border-white/20 text-white' };
        }
    }
    return { label: 'เผยแพร่อยู่', style: 'bg-emerald-500/90 backdrop-blur-md border border-white/20 text-white' };
};

const AnnouncementManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [announcements, setAnnouncements] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [newAnnouncement, setNewAnnouncement] = useState({
        title: '',
        content: '',
        target_group: 'All',
        image_url: '',
        is_active: true,
        pinned: false,
        show_popup: true, // Default to true
        popup_snooze_days: 1, // Default to 1 (Temporary snooze 1 day)
        start_date: '',
        end_date: ''
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [isTargetGroupDropdownOpen, setIsTargetGroupDropdownOpen] = useState(false);

    // UI states
    const [searchTerm, setSearchTerm] = useState('');
    const [filterGroup, setFilterGroup] = useState('All');
    const [filterActive, setFilterActive] = useState('All'); // 'All', 'Active', 'Draft'
    const [sortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [previewMode, setPreviewMode] = useState('popup'); // Default to 'popup' when show_popup is true
    const [uploading, setUploading] = useState(false);
    const [previewAnnouncement, setPreviewAnnouncement] = useState(null); // to show full preview modal
    const [visibleCount, setVisibleCount] = useState(6);
    const [imageErrors, setImageErrors] = useState({});
    const handleImageError = (url) => {
        if (!url) return;
        setImageErrors(prev => ({ ...prev, [url]: true }));
    };

    useEffect(() => {
        setVisibleCount(6);
    }, [searchTerm, filterActive, filterGroup]);

    const fileInputRef = useRef(null);

    // Track uploaded images in current session for cleanup on cancel
    const [sessionUploadedImages, setSessionUploadedImages] = useState([]);
    const [originalImage, setOriginalImage] = useState('');

    const fetchAnnouncements = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/announcements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnnouncements(res.data);
        } catch (err) {
            console.error('Failed to fetch announcements:', err);
        }
    }, [token]);

    useEffect(() => {
        fetchAnnouncements();
    }, [fetchAnnouncements]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
            return;
        }

        const formData = new FormData();
        formData.append('image', file);

        try {
            setUploading(true);
            setError('');

            const res = await axios.post(`${API_URL}/api/upload/announcement`, formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            const newUrl = res.data.url;
            setNewAnnouncement(prev => ({ ...prev, image_url: newUrl }));
            setSessionUploadedImages(prev => [...prev, newUrl]);

            if (e.target) e.target.value = '';
        } catch (err) {
            console.error('Upload Error:', err);
            setError(err.response?.data?.message || 'ไม่สามารถอัปโหลดรูปภาพได้');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = () => {
        setNewAnnouncement(prev => ({ ...prev, image_url: '' }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!newAnnouncement.title.trim()) {
            setError('กรุณาระบุหัวข้อประกาศ');
            return;
        }
        if (!newAnnouncement.content.trim()) {
            setError('กรุณาระบุเนื้อหาประกาศ');
            return;
        }

        setError('');
        try {
            if (editingAnnouncement) {
                await axios.put(`${API_URL}/api/announcements/${editingAnnouncement.id}`, newAnnouncement, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('อัปเดตประกาศเรียบร้อยแล้ว');
            } else {
                await axios.post(`${API_URL}/api/announcements`, newAnnouncement, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('สร้างประกาศใหม่เรียบร้อยแล้ว');
            }

            // Cleanup unused images
            const imagesToDelete = sessionUploadedImages.filter(img => img !== newAnnouncement.image_url);
            for (const imgUrl of imagesToDelete) {
                try {
                    await axios.post(`${API_URL}/api/delete/image`, { url: imgUrl }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                } catch (err) { console.error('Failed to cleanup image:', err); }
            }

            setSessionUploadedImages([]);
            setShowSuccessPopup(true);
            setIsModalOpen(false);
            fetchAnnouncements();
        } catch (err) {
            console.error('Save Error:', err);
            setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API_URL}/api/announcements/${deleteTargetId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('ลบประกาศเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
            setShowDeleteConfirm(false);
            fetchAnnouncements();
        } catch (err) {
            console.error('Delete Error:', err);
            setErrorMessage(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบข้อมูล');
            setShowErrorPopup(true);
            setShowDeleteConfirm(false);
        }
    };

    const openModal = (ann = null) => {
        if (ann) {
            setEditingAnnouncement(ann);
            const isPopupEnabled = ann.show_popup === 1 || ann.show_popup === true;
            setNewAnnouncement({
                title: ann.title,
                content: ann.content,
                target_group: ann.target_group,
                image_url: ann.image_url || '',
                is_active: ann.is_active === 1 || ann.is_active === true,
                pinned: ann.pinned === 1 || ann.pinned === true,
                show_popup: isPopupEnabled,
                popup_snooze_days: ann.popup_snooze_days,
                start_date: formatDateForInput(ann.start_date),
                end_date: formatDateForInput(ann.end_date)
            });
            setOriginalImage(ann.image_url || '');
            setPreviewMode(isPopupEnabled ? 'popup' : 'post');
        } else {
            setEditingAnnouncement(null);
            setNewAnnouncement({
                title: '',
                content: '',
                target_group: 'All',
                image_url: '',
                is_active: true,
                pinned: false,
                show_popup: true, // Default to true
                popup_snooze_days: 1, // Default to 1 (Temporary snooze 1 day)
                start_date: '',
                end_date: ''
            });
            setOriginalImage('');
            setPreviewMode('popup'); // Default to popup
        }
        setSessionUploadedImages([]);
        setError('');
        setIsModalOpen(true);
    };

    const closeModal = async () => {
        // If canceled, delete all newly uploaded images in this session
        if (sessionUploadedImages.length > 0) {
            for (const imgUrl of sessionUploadedImages) {
                if (imgUrl !== originalImage) {
                    try {
                        await axios.post(`${API_URL}/api/delete/image`, { url: imgUrl }, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    } catch (err) { console.error('Failed to cleanup image on close:', err); }
                }
            }
        }
        setIsModalOpen(false);
        setEditingAnnouncement(null);
        setSessionUploadedImages([]);
    };

    const handleQuickToggle = async (ann, field) => {
        const updated = {
            ...ann,
            is_active: ann.is_active === 1 || ann.is_active === true,
            pinned: ann.pinned === 1 || ann.pinned === true,
            show_popup: ann.show_popup === 1 || ann.show_popup === true,
            [field]: !(ann[field] === 1 || ann[field] === true)
        };

        try {
            await axios.put(`${API_URL}/api/announcements/${ann.id}`, updated, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchAnnouncements();
        } catch (err) {
            console.error('Failed to toggle status:', err);
        }
    };

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'announcements');
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    // Filter and Sort Announcements
    const filteredAnnouncements = announcements.filter(ann => {
        const matchesSearch =
            ann.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            ann.content.toLowerCase().includes(searchTerm.toLowerCase());

        const annTarget = (ann.target_group || '').toLowerCase();
        const filterGrp = (filterGroup || '').toLowerCase();
        const matchesGroup = 
            filterGrp === 'all' || 
            annTarget === 'all' || 
            annTarget === filterGrp || 
            (filterGrp === 'resident' && annTarget === 'user') ||
            (filterGrp === 'user' && annTarget === 'resident');

        const isActiveVal = ann.is_active === 1 || ann.is_active === true;
        const isPinnedVal = ann.pinned === 1 || ann.pinned === true;
        const isPopupVal = ann.show_popup === 1 || ann.show_popup === true;

        const now = Date.now();
        let isExpiredVal = false;
        if (isActiveVal && ann.end_date) {
            const d = new Date(ann.end_date);
            const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
            isExpiredVal = now > end;
        }

        let isScheduledVal = false;
        if (isActiveVal && ann.start_date) {
            const d = new Date(ann.start_date);
            const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
            isScheduledVal = now < start;
        }

        let matchesActive = true;
        if (filterActive === 'Active') {
            matchesActive = isActiveVal && !isExpiredVal && !isScheduledVal;
        } else if (filterActive === 'Draft') {
            matchesActive = !isActiveVal;
        } else if (filterActive === 'Pinned') {
            matchesActive = isPinnedVal;
        } else if (filterActive === 'Popup') {
            matchesActive = isPopupVal;
        } else if (filterActive === 'Expired') {
            matchesActive = isExpiredVal;
        } else if (filterActive === 'Scheduled') {
            matchesActive = isScheduledVal;
        }

        return matchesSearch && matchesGroup && matchesActive;
    });

    const sortedAnnouncements = [...filteredAnnouncements].sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle boolean values mapping for sorting (pinned first)
        if (sortConfig.key === 'pinned') {
            aValue = a.pinned ? 1 : 0;
            bValue = b.pinned ? 1 : 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const visibleAnnouncements = React.useMemo(() => {
        return sortedAnnouncements.slice(0, visibleCount);
    }, [sortedAnnouncements, visibleCount]);

    // Statistics calculations
    const stats = React.useMemo(() => {
        const now = Date.now();
        let total = announcements.length;
        let draft = announcements.filter(a => !(a.is_active === 1 || a.is_active === true)).length;

        let expired = announcements.filter(a => {
            const isActive = a.is_active === 1 || a.is_active === true;
            if (!isActive) return false;
            if (a.end_date) {
                const d = new Date(a.end_date);
                const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
                return now > end;
            }
            return false;
        }).length;

        let active = announcements.filter(a => {
            const isActive = a.is_active === 1 || a.is_active === true;
            if (!isActive) return false;

            // Check if expired
            if (a.end_date) {
                const d = new Date(a.end_date);
                const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
                if (now > end) return false;
            }

            // Check if scheduled
            if (a.start_date) {
                const d = new Date(a.start_date);
                const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
                if (now < start) return false;
            }

            return true;
        }).length;

        let scheduled = announcements.filter(a => {
            const isActive = a.is_active === 1 || a.is_active === true;
            if (!isActive) return false;
            if (a.start_date) {
                const d = new Date(a.start_date);
                const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
                return now < start;
            }
            return false;
        }).length;

        let pinned = announcements.filter(a => a.pinned === 1 || a.pinned === true).length;
        let popup = announcements.filter(a => a.show_popup === 1 || a.show_popup === true).length;

        return { total, active, draft, expired, scheduled, pinned, popup };
    }, [announcements]);

    return (
        <div className="min-h-screen bg-slate-50/50">
            <Navbar role={role} username={username} onLogout={() => setToken('')} permissions={permissions} />

            <main className="max-w-7xl mx-auto px-4 md:px-8 pb-10">

                {/* Upper Header Section */}
                <div className="mb-4 mt-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-base font-black text-slate-800 tracking-tight">
                            จัดการประกาศ
                        </h1>
                        {canEdit && (
                            <button
                                onClick={() => openModal()}
                                className="flex items-center gap-1.5 bg-gradient-to-tr from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-3.5 py-1.5 rounded-full font-bold text-xs shadow-md shadow-blue-500/10 active:scale-95 transition-all cursor-pointer border border-blue-500/10 shrink-0"
                            >
                                <Plus className="w-3.5 h-3.5 shrink-0" />
                                <span>เพิ่มประกาศ</span>
                            </button>
                        )}
                    </div>
                    {/* <p className="text-slate-500 text-xs font-medium mt-1">
                        สร้าง แก้ไข และเผยแพร่ข้อมูลข่าวสารสำคัญไปยังกลุ่มผู้ใช้งานที่ระบุ
                    </p> */}
                </div>

                {/* Compact Interactive Dashboard Stats & Filters */}
                <div className="flex flex-col gap-3 mb-5">
                    {/* Interactive Stats Toolbar */}
                    <div className="flex overflow-x-auto whitespace-nowrap scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0 gap-2 pb-1.5 items-center">
                        {/* Stat Pill: All */}
                        <button
                            onClick={() => { setFilterActive('All'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'All' && filterGroup === 'All'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>ทั้งหมด</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'All' && filterGroup === 'All' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.total}</span>
                        </button>

                        {/* Stat Pill: Active */}
                        <button
                            onClick={() => { setFilterActive('Active'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Active'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>เผยแพร่อยู่</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Active' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.active}</span>
                        </button>

                        {/* Stat Pill: Pinned */}
                        <button
                            onClick={() => { setFilterActive('Pinned'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Pinned'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>ปักหมุด</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Pinned' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.pinned}</span>
                        </button>

                        {/* Stat Pill: Popup */}
                        <button
                            onClick={() => { setFilterActive('Popup'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Popup'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>แสดง Popup</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Popup' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.popup}</span>
                        </button>

                        {/* Stat Pill: Scheduled */}
                        <button
                            onClick={() => { setFilterActive('Scheduled'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Scheduled'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>ตั้งเวลา</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Scheduled' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.scheduled}</span>
                        </button>

                        {/* Stat Pill: Draft */}
                        <button
                            onClick={() => { setFilterActive('Draft'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Draft'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>แบบร่าง</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Draft' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.draft}</span>
                        </button>

                        {/* Stat Pill: Expired */}
                        <button
                            onClick={() => { setFilterActive('Expired'); setFilterGroup('All'); }}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wide transition-all flex items-center gap-2 border cursor-pointer shrink-0 ${filterActive === 'Expired'
                                ? 'bg-slate-900 border-slate-900 text-white shadow-sm shadow-slate-900/10'
                                : 'bg-slate-100 border-transparent text-slate-600 hover:bg-slate-200/60'
                                }`}
                        >
                            <span>หมดอายุ</span>
                            <span className={`px-1.5 py-0.5 rounded-full text-[8.5px] font-black ${filterActive === 'Expired' ? 'bg-white/25 text-white' : 'bg-slate-200/60 text-slate-500'
                                }`}>{stats.expired}</span>
                        </button>
                    </div>

                    {/* Search & Target Filters Section */}
                    <div className="flex gap-2 items-center mb-6 w-full flex-nowrap">
                        <div className="relative flex-1">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="ค้นหาประกาศ..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-10 py-2 text-xs font-semibold text-slate-700 placeholder-slate-400 bg-slate-100 hover:bg-slate-200/50 border-0 focus:ring-1.5 focus:ring-slate-300 focus:outline-none transition-all rounded-lg h-9"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-450 hover:text-slate-600 transition-colors cursor-pointer border-none bg-transparent"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        <div className="shrink-0 flex items-center justify-end">
                            {/* Target group filter */}
                            <CustomFilterSelect
                                label="เป้าหมาย"
                                value={filterGroup}
                                onChange={setFilterGroup}
                                options={[
                                    { value: 'All', label: 'ทั้งหมด' },
                                    { value: 'Resident', label: 'Resident' },
                                    { value: 'Guard', label: 'Guard' },
                                    { value: 'Staff', label: 'Staff' }
                                ]}
                            />
                        </div>
                    </div>
                </div>

                {/* Announcement Cards Grid */}
                {sortedAnnouncements.length === 0 ? (
                    <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center shadow-sm">
                        <div className="w-16 h-16 rounded-full bg-slate-50 text-slate-400 flex items-center justify-center mx-auto mb-4 border border-slate-100">
                            <Megaphone className="w-8 h-8 opacity-60" />
                        </div>
                        <h3 className="text-base font-black text-slate-700">ไม่พบประกาศ</h3>
                        <p className="text-xs font-semibold text-slate-400 mt-1">
                            ไม่มีประกาศที่ตรงกับเงื่อนไขการค้นหาของคุณ ลองเปลี่ยนตัวกรองหรือสร้างประกาศใหม่
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {visibleAnnouncements.map((ann) => {
                            const isActiveVal = ann.is_active === 1 || ann.is_active === true;
                            const isPinnedVal = ann.pinned === 1 || ann.pinned === true;
                            const isPopupVal = ann.show_popup === 1 || ann.show_popup === true;

                            return (
                                <div
                                    key={ann.id}
                                    className={`bg-white border rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col group relative ${!isActiveVal ? 'opacity-80 border-slate-100' : 'border-slate-200/80'
                                        }`}
                                >
                                    {/* Status Overlay at top-right */}
                                    <div className="absolute top-3 right-3 z-10">
                                        {(() => {
                                            const status = getScheduleStatus(ann);
                                            return (
                                                <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm ${status.style}`}>
                                                    {status.label}
                                                </span>
                                            );
                                        })()}
                                    </div>

                                    {/* Banner Image or Placeholder */}
                                    <div className="h-56 relative overflow-hidden bg-slate-50 shrink-0 border-b border-slate-100 flex items-center justify-center">
                                        {ann.image_url && !imageErrors[ann.image_url] ? (
                                            <img
                                                src={`${API_URL}${ann.image_url}`}
                                                alt={ann.title}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-all duration-500"
                                                onError={() => handleImageError(ann.image_url)}
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-slate-100/50 flex items-center justify-center group-hover:scale-102 transition-transform duration-500">
                                                <Megaphone className="w-12 h-12 text-slate-300 rotate-[-15deg]" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Card Content */}
                                    <div className="p-5 flex-1 flex flex-col justify-between">
                                        <div className="min-w-0">
                                            {/* Target Group pill below image */}
                                            <div className="flex items-center gap-1.5 mb-2.5">
                                                <span className={`px-2 py-0.5 rounded text-[9.5px] font-black tracking-wider ${ann.target_group === 'All' ? 'bg-slate-100 text-slate-600' :
                                                    (ann.target_group === 'Resident' || ann.target_group === 'User') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' :
                                                        ann.target_group === 'Guard' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' :
                                                            'bg-amber-50 text-amber-700 border border-amber-100'
                                                    }`}>
                                                    {ann.target_group === 'All' ? 'All Groups' :
                                                        (ann.target_group === 'Resident' || ann.target_group === 'User') ? 'Resident' :
                                                            ann.target_group === 'Guard' ? 'Guard' : 'Staff'}
                                                </span>
                                            </div>

                                            <h2 className="text-sm font-black text-slate-800 line-clamp-1 mb-2 group-hover:text-blue-600 transition-colors flex items-center gap-1.5" title={ann.title}>
                                                {isPinnedVal && <Pin className="w-3.5 h-3.5 text-indigo-500 rotate-45 shrink-0" />}
                                                {isPopupVal && <Bell className="w-3.5 h-3.5 text-pink-500 shrink-0" />}
                                                <span className="truncate flex-1">{ann.title}</span>
                                            </h2>
                                            <p className="text-[12px] font-semibold text-slate-500 line-clamp-3 leading-relaxed mb-4 whitespace-pre-wrap">
                                                {ann.content}
                                            </p>

                                            {/* Schedule Info */}
                                            {(ann.start_date || ann.end_date) && (
                                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 bg-slate-50 border border-slate-100/50 rounded-lg p-1.5 mb-3.5 w-fit">
                                                    <Calendar className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                                    <span className="truncate">
                                                        {ann.start_date ? new Date(ann.start_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : 'ทันที'}
                                                        {' - '}
                                                        {ann.end_date ? new Date(ann.end_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : 'ไม่มีกำหนด'}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="border-t border-slate-100 pt-4 flex flex-col gap-3">
                                            {/* Creator info and Date */}
                                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-400">
                                                <span className="flex items-center gap-1">
                                                    <User className="w-3.5 h-3.5" />
                                                    By : {ann.created_by}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    {new Date(ann.created_at).toLocaleDateString('th-TH', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })} น.
                                                </span>
                                            </div>

                                            {/* Quick Actions (Toggles) if admin */}
                                            {canEdit && (
                                                <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl p-1 gap-1.5 text-[10px] font-black text-slate-500">
                                                    <button
                                                        onClick={() => handleQuickToggle(ann, 'is_active')}
                                                        className={`flex-1 py-1 rounded-lg transition-all text-center cursor-pointer ${isActiveVal
                                                            ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50 font-black'
                                                            : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                                            }`}
                                                    >
                                                        เผยแพร่
                                                    </button>
                                                    <button
                                                        onClick={() => handleQuickToggle(ann, 'pinned')}
                                                        className={`flex-1 py-1 rounded-lg transition-all text-center cursor-pointer ${isPinnedVal
                                                            ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50 font-black'
                                                            : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                                            }`}
                                                    >
                                                        ปักหมุด
                                                    </button>
                                                    <button
                                                        onClick={() => handleQuickToggle(ann, 'show_popup')}
                                                        className={`flex-1 py-1 rounded-lg transition-all text-center cursor-pointer ${isPopupVal
                                                            ? 'bg-white text-slate-800 shadow-sm border border-slate-200/50 font-black'
                                                            : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                                            }`}
                                                    >
                                                        แสดง Popup
                                                    </button>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex items-center gap-1.5 mt-1.5">
                                                <button
                                                    onClick={() => setPreviewAnnouncement(ann)}
                                                    className="flex-1 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold text-[11px] hover:bg-slate-200 transition-colors flex items-center justify-center gap-1 active:scale-[0.98] cursor-pointer"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    Preview
                                                </button>

                                                {canEdit && (
                                                    <>
                                                        <button
                                                            onClick={() => openModal(ann)}
                                                            className="px-3 py-2 bg-blue-50 text-blue-600 border border-blue-100 hover:bg-blue-100 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 active:scale-[0.98] cursor-pointer"
                                                        >
                                                            <Pencil className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setDeleteTargetId(ann.id);
                                                                setShowDeleteConfirm(true);
                                                            }}
                                                            className="px-3 py-2 bg-rose-50 text-rose-600 border border-rose-100 hover:bg-rose-100 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 active:scale-[0.98] cursor-pointer"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                        {sortedAnnouncements.length > visibleCount && (
                            <div className="flex justify-center mt-2 mb-4">
                                <button
                                    type="button"
                                    onClick={() => setVisibleCount((prev) => prev + 6)}
                                    className="px-6 py-2.5 bg-white border border-slate-200 hover:border-slate-350 hover:bg-slate-50 text-slate-655 hover:text-slate-800 rounded-full font-bold text-xs shadow-sm hover:shadow active:scale-[0.98] transition-all cursor-pointer flex items-center gap-1.5"
                                >
                                    <span>แสดงเพิ่มเติม</span>
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* FORM MODAL WITH SPLIT LIVE PREVIEW */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[300] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[850px] flex flex-col lg:flex-row overflow-hidden border border-slate-100 animate-in fade-in-50 zoom-in-95 duration-200 max-h-[90vh]">

                        {/* Form Inputs (Left) */}
                        <form onSubmit={handleSave} className="w-full lg:w-[450px] lg:shrink-0 flex-1 min-h-0 flex flex-col overflow-hidden border-b lg:border-b-0 lg:border-r border-slate-100 max-h-full">
                            <div className="p-4 px-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                                <div>
                                    <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                        {editingAnnouncement ? 'แก้ไขข้อมูลประกาศ' : 'สร้างประกาศประชาสัมพันธ์'}
                                    </h2>
                                    <p className="text-xs font-medium text-slate-400 mt-0.5">กรอกข้อมูลรายละเอียดประกาศประชาสัมพันธ์</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors border-none bg-transparent"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="flex-1 p-6 md:p-8 overflow-y-auto space-y-4 min-h-0">
                                {/* Title */}
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 tracking-wider mb-1.5">หัวข้อประกาศ <span className="text-rose-500">*</span></label>
                                    <input
                                        type="text"
                                        placeholder="เช่น แจ้งเปลี่ยนสติ๊กเกอร์จอดรถรอบปี 2569"
                                        value={newAnnouncement.title}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                                        className="w-full h-9 px-3 py-1.5 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-slate-350 transition-colors placeholder:text-slate-350"
                                    />
                                </div>

                                {/* Content */}
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 tracking-wider mb-1.5">เนื้อหาประกาศ <span className="text-rose-500">*</span></label>
                                    <textarea
                                        rows={4}
                                        placeholder="พิมพ์เนื้อหาข่าวสารประชาสัมพันธ์ที่นี่..."
                                        value={newAnnouncement.content}
                                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                                        className="w-full px-3 py-2 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-slate-350 transition-colors placeholder:text-slate-350 resize-y"
                                    />
                                </div>

                                {/* Row target & upload */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Target Group */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 tracking-wider mb-1.5">เป้าหมาย</label>
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setIsTargetGroupDropdownOpen(!isTargetGroupDropdownOpen)}
                                                className="w-full h-9 px-3 py-1.5 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none hover:border-slate-300 cursor-pointer flex items-center justify-between transition-colors shadow-sm"
                                            >
                                                <span className="truncate">
                                                    {newAnnouncement.target_group === 'All' ? 'All' :
                                                        (newAnnouncement.target_group === 'Resident' || newAnnouncement.target_group === 'User') ? 'Resident' :
                                                            newAnnouncement.target_group === 'Guard' ? 'Guard' :
                                                                newAnnouncement.target_group === 'Staff' ? 'Staff' : 'เลือกเป้าหมาย'}
                                                </span>
                                                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isTargetGroupDropdownOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                            </button>

                                            {isTargetGroupDropdownOpen && (
                                                <>
                                                    <div className="fixed inset-0 z-[310]" onClick={() => setIsTargetGroupDropdownOpen(false)} />
                                                    <div className="absolute top-full mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[320] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                        <div className="py-1">
                                                            {[
                                                                { label: 'All', value: 'All' },
                                                                { label: 'Resident', value: 'Resident' },
                                                                { label: 'Guard', value: 'Guard' },
                                                                { label: 'Staff', value: 'Staff' }
                                                            ].map(opt => {
                                                                const isSel = newAnnouncement.target_group === opt.value || 
                                                                             (opt.value === 'Resident' && newAnnouncement.target_group === 'User');
                                                                return (
                                                                    <button
                                                                        key={opt.value}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setNewAnnouncement({ ...newAnnouncement, target_group: opt.value });
                                                                            setIsTargetGroupDropdownOpen(false);
                                                                        }}
                                                                        className={`w-full text-left px-4 py-2 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer border-none bg-transparent ${isSel ? 'bg-blue-50 text-blue-600 font-bold' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span>{opt.label}</span>
                                                                        {isSel && <Check className="w-3.5 h-3.5 text-blue-600" />}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Active state */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 tracking-wider mb-1.5">สถานะ</label>
                                        <div className="bg-slate-100 p-0.5 rounded-lg flex h-9">
                                            <button
                                                type="button"
                                                onClick={() => setNewAnnouncement({ ...newAnnouncement, is_active: true })}
                                                className={`flex-1 flex items-center justify-center rounded-[6px] text-xs font-bold transition-all cursor-pointer border-0 ${newAnnouncement.is_active
                                                    ? 'bg-white text-emerald-600 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-700 bg-transparent'
                                                    }`}
                                            >
                                                เผยแพร่
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setNewAnnouncement({ ...newAnnouncement, is_active: false })}
                                                className={`flex-1 flex items-center justify-center rounded-[6px] text-xs font-bold transition-all cursor-pointer border-0 ${!newAnnouncement.is_active
                                                    ? 'bg-white text-slate-800 shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-700 bg-transparent'
                                                    }`}
                                            >
                                                แบบร่าง
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Banner Uploader */}
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 tracking-wider mb-1.5">รูปแบนเนอร์ประกาศ (ถ้ามี)</label>
                                    {newAnnouncement.image_url ? (
                                        <div className="relative rounded-2xl overflow-hidden border border-slate-200/60 bg-slate-50 group flex justify-center items-center p-2 min-h-[120px]">
                                            {imageErrors[newAnnouncement.image_url] ? (
                                                <div className="w-full py-5 px-4 bg-amber-50/80 border border-amber-100/70 rounded-xl flex flex-col items-center justify-center text-center gap-1.5">
                                                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
                                                    <span className="text-[11px] font-bold text-amber-800">พบปัญหา: หารูปภาพประกาศไม่เจอ</span>
                                                    <span className="text-[9px] text-amber-600 font-semibold">กรุณาลบและอัปโหลดรูปภาพใหม่เพื่อทำการแก้ไข</span>
                                                </div>
                                            ) : (
                                                <img
                                                    src={`${API_URL}${newAnnouncement.image_url}`}
                                                    alt="Uploaded Banner Preview"
                                                    className="w-full h-auto max-h-48 object-contain"
                                                    onError={() => handleImageError(newAnnouncement.image_url)}
                                                />
                                            )}
                                            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2 transition-all duration-200 z-10">
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className="p-2 bg-white text-blue-600 rounded-xl shadow-md hover:bg-slate-100 transition-all font-black text-[11px] flex items-center gap-1 cursor-pointer"
                                                >
                                                    <Upload className="w-3.5 h-3.5" /> เปลี่ยนรูป
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleDeleteImage}
                                                    className="p-2 bg-rose-600 text-white rounded-xl shadow-md hover:bg-rose-700 transition-all font-black text-[11px] flex items-center gap-1 cursor-pointer"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" /> ลบรูปออก
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            onClick={() => fileInputRef.current?.click()}
                                            className="border-2 border-dashed border-slate-200 hover:border-blue-400 rounded-2xl p-6 text-center cursor-pointer transition-colors hover:bg-slate-50/50 flex flex-col items-center justify-center gap-2"
                                        >
                                            {uploading ? (
                                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                                            ) : (
                                                <ImageIcon className="w-7 h-7 text-slate-400" />
                                            )}
                                            <div className="text-[11px] font-black text-slate-600">
                                                {uploading ? 'กำลังอัปโหลด...' : 'คลิกเพื่อเลือกไฟล์รูปภาพแบนเนอร์'}
                                            </div>
                                            <p className="text-[9px] text-slate-400">ขนาดแนะนำ 1200x630px หรือสัดส่วน 16:9 (ไฟล์ภาพ .PNG, .JPG)</p>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        className="hidden"
                                        accept="image/*"
                                    />
                                </div>

                                {/* Publish Date Schedule */}
                                <div className="border-t border-slate-100 pt-4 space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">กำหนดเวลาเผยแพร่ประกาศ</h3>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 mb-1">วันที่เริ่มต้น</label>
                                            <div className="relative">
                                                <DatePicker
                                                    selected={newAnnouncement.start_date ? new Date(newAnnouncement.start_date) : null}
                                                    onChange={(date) => setNewAnnouncement({ ...newAnnouncement, start_date: date ? formatDateForInput(date) : '' })}
                                                    locale="th"
                                                    dateFormat="d MMM yy"
                                                    minDate={new Date()}
                                                    popperPlacement="bottom-start"
                                                    popperClassName="!z-[500]"
                                                    wrapperClassName="w-full"
                                                    renderCustomHeader={({
                                                        date,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => {
                                                        const months = [
                                                            'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                                                            'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
                                                        ];
                                                        return (
                                                            <div className="flex items-center justify-between px-3 py-1.5 bg-white border-b border-slate-100">
                                                                <button
                                                                    type="button"
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    className="p-1 hover:bg-slate-50 rounded-lg text-slate-500 hover:text-slate-805 disabled:opacity-30 transition-colors cursor-pointer border-none bg-transparent"
                                                                >
                                                                    <ChevronDown className="w-3.5 h-3.5 rotate-90" />
                                                                </button>
                                                                <span className="text-[11px] font-black text-slate-700 leading-none">
                                                                    {months[date.getMonth()]} {date.getFullYear() + 543}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    className="p-1 hover:bg-slate-50 rounded-lg text-slate-500 hover:text-slate-805 disabled:opacity-30 transition-colors cursor-pointer border-none bg-transparent"
                                                                >
                                                                    <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                                                                </button>
                                                            </div>
                                                        );
                                                    }}
                                                    customInput={
                                                        <DateInputButton
                                                            icon={Calendar}
                                                            placeholder="dd/mm/yyyy"
                                                            displayValue={formatThaiBuddhistDate(newAnnouncement.start_date)}
                                                            onClear={() => setNewAnnouncement({ ...newAnnouncement, start_date: '' })}
                                                        />
                                                    }
                                                />
                                            </div>
                                            <p className="text-[9px] text-slate-400 mt-1">เว้นว่างไว้หากต้องการให้เผยแพร่ทันที</p>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 mb-1">วันที่สิ้นสุด</label>
                                            <div className="relative">
                                                <DatePicker
                                                    selected={newAnnouncement.end_date ? new Date(newAnnouncement.end_date) : null}
                                                    onChange={(date) => setNewAnnouncement({ ...newAnnouncement, end_date: date ? formatDateForInput(date) : '' })}
                                                    locale="th"
                                                    dateFormat="d MMM yy"
                                                    minDate={newAnnouncement.start_date ? new Date(newAnnouncement.start_date) : new Date()}
                                                    popperPlacement="bottom-start"
                                                    popperClassName="!z-[500]"
                                                    wrapperClassName="w-full"
                                                    renderCustomHeader={({
                                                        date,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => {
                                                        const months = [
                                                            'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
                                                            'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
                                                        ];
                                                        return (
                                                            <div className="flex items-center justify-between px-3 py-1.5 bg-white border-b border-slate-100">
                                                                <button
                                                                    type="button"
                                                                    onClick={decreaseMonth}
                                                                    disabled={prevMonthButtonDisabled}
                                                                    className="p-1 hover:bg-slate-50 rounded-lg text-slate-500 hover:text-slate-805 disabled:opacity-30 transition-colors cursor-pointer border-none bg-transparent"
                                                                >
                                                                    <ChevronDown className="w-3.5 h-3.5 rotate-90" />
                                                                </button>
                                                                <span className="text-[11px] font-black text-slate-700 leading-none">
                                                                    {months[date.getMonth()]} {date.getFullYear() + 543}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={increaseMonth}
                                                                    disabled={nextMonthButtonDisabled}
                                                                    className="p-1 hover:bg-slate-50 rounded-lg text-slate-500 hover:text-slate-805 disabled:opacity-30 transition-colors cursor-pointer border-none bg-transparent"
                                                                >
                                                                    <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                                                                </button>
                                                            </div>
                                                        );
                                                    }}
                                                    customInput={
                                                        <DateInputButton
                                                            icon={Calendar}
                                                            placeholder="dd/mm/yyyy"
                                                            displayValue={formatThaiBuddhistDate(newAnnouncement.end_date)}
                                                            onClear={() => setNewAnnouncement({ ...newAnnouncement, end_date: '' })}
                                                        />
                                                    }
                                                />
                                            </div>
                                            <p className="text-[9px] text-slate-400 mt-1">เว้นว่างไว้เพื่อแสดงผลไม่มีกำหนด</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Extra configurations */}
                                <div className="border-t border-slate-100 pt-4 space-y-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">การตั้งค่าเพิ่มเติม</h3>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        {/* Pinned toggle */}
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Pin className="w-4 h-4 text-slate-400 rotate-45" />
                                                <div>
                                                    <div className="text-[12px] font-black text-slate-700">ปักหมุดประกาศนี้</div>
                                                    <div className="text-[10px] font-semibold text-slate-400">แสดงผลที่ด้านบนสุดของรายการประกาศเสมอ</div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setNewAnnouncement(prev => ({ ...prev, pinned: !prev.pinned }))}
                                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                    newAnnouncement.pinned ? 'bg-blue-600' : 'bg-slate-200'
                                                }`}
                                            >
                                                <span
                                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                        newAnnouncement.pinned ? 'translate-x-4' : 'translate-x-0'
                                                    }`}
                                                />
                                            </button>
                                        </div>

                                        {/* Show Popup toggle */}
                                        <div className="flex items-center justify-between border-t border-slate-50 pt-3">
                                            <div className="flex items-center gap-2">
                                                <Bell className="w-4 h-4 text-slate-400" />
                                                <div>
                                                    <div className="text-[12px] font-black text-slate-700">แสดงเป็น Popup เมื่อเข้าสู่ระบบ</div>
                                                    <div className="text-[10px] font-semibold text-slate-400">เมื่อผู้ใช้เป้าหมายเข้าระบบจะแสดงกล่องป๊อปอัปนี้ทันที</div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const checked = !newAnnouncement.show_popup;
                                                    setNewAnnouncement(prev => ({
                                                        ...prev,
                                                        show_popup: checked,
                                                        popup_snooze_days: checked && prev.popup_snooze_days <= 0 && prev.popup_snooze_days !== -1 ? 1 : prev.popup_snooze_days
                                                    }));
                                                    setPreviewMode(checked ? 'popup' : 'post');
                                                }}
                                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                    newAnnouncement.show_popup ? 'bg-blue-600' : 'bg-slate-200'
                                                }`}
                                            >
                                                <span
                                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                        newAnnouncement.show_popup ? 'translate-x-4' : 'translate-x-0'
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Dismiss option / Snooze settings (displays only if show_popup is true) */}
                                    {newAnnouncement.show_popup && (
                                        <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-3 space-y-2.5 animate-in slide-in-from-top-2 duration-200">
                                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                ระยะเวลาการปิดแจ้งเตือน
                                            </div>

                                            <div className="grid grid-cols-3 gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => setNewAnnouncement({ ...newAnnouncement, popup_snooze_days: 0 })}
                                                    className={`h-8 px-2.5 border rounded-lg text-[11px] font-bold transition-all cursor-pointer ${newAnnouncement.popup_snooze_days === 0
                                                        ? 'bg-white border-blue-500 text-blue-600 shadow-sm font-extrabold'
                                                        : 'bg-transparent border-slate-200 text-slate-500 hover:bg-white/80'
                                                        }`}
                                                >
                                                    แสดงทุกครั้ง
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => setNewAnnouncement({ ...newAnnouncement, popup_snooze_days: -1 })}
                                                    className={`h-8 px-2.5 border rounded-lg text-[11px] font-bold transition-all cursor-pointer ${newAnnouncement.popup_snooze_days === -1
                                                        ? 'bg-white border-blue-500 text-blue-600 shadow-sm font-extrabold'
                                                        : 'bg-transparent border-slate-200 text-slate-500 hover:bg-white/80'
                                                        }`}
                                                >
                                                    ปิดถาวร
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const currentVal = newAnnouncement.popup_snooze_days;
                                                        setNewAnnouncement({
                                                            ...newAnnouncement,
                                                            popup_snooze_days: currentVal > 0 ? currentVal : 1
                                                        });
                                                    }}
                                                    className={`h-8 px-2.5 border rounded-lg text-[11px] font-bold transition-all cursor-pointer ${newAnnouncement.popup_snooze_days > 0
                                                        ? 'bg-white border-blue-500 text-blue-600 shadow-sm font-extrabold'
                                                        : 'bg-transparent border-slate-200 text-slate-550 hover:bg-white/80'
                                                        }`}
                                                >
                                                    ปิดชั่วคราว
                                                </button>
                                            </div>

                                            {newAnnouncement.popup_snooze_days > 0 && (
                                                <div className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-lg animate-in slide-in-from-top-1 duration-150">
                                                    <span className="text-[11px] font-bold text-slate-500 shrink-0">ระบุจำนวนวันที่จะซ่อน Popup:</span>
                                                    <div className="flex items-center gap-1.5 flex-1 max-w-[120px]">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={365}
                                                            value={newAnnouncement.popup_snooze_days}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 1;
                                                                setNewAnnouncement({ ...newAnnouncement, popup_snooze_days: Math.max(1, val) });
                                                            }}
                                                            className="w-14 text-center border border-slate-200 rounded-md py-0.5 h-6.5 text-[12px] font-bold focus:outline-none focus:border-blue-500"
                                                        />
                                                        <span className="text-[11px] font-bold text-slate-500">วัน</span>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="text-[9.5px] font-semibold text-slate-400/90 mt-0.5 flex items-start gap-1">
                                                <AlertCircle className="w-3.5 h-3.5 text-blue-500 shrink-0 mt-0.5" />
                                                <span>
                                                    {newAnnouncement.popup_snooze_days === 0 && 'ผู้ใช้จะไม่สามารถกด "ไม่ต้องแสดงอีก" ได้ มีเพียงปุ่มปิดธรรมดาเท่านั้น'}
                                                    {newAnnouncement.popup_snooze_days === -1 && 'ปุ่มของผู้ใช้จะเขียนว่า "ไม่ต้องแสดงประกาศนี้อีก" เมื่อกดแล้วจะไม่เจออีกเลย'}
                                                    {newAnnouncement.popup_snooze_days > 0 && `ปุ่มของผู้ใช้จะเขียนว่า "ไม่ต้องแสดงอีก ${newAnnouncement.popup_snooze_days} วัน" เมื่อกดแล้วจะซ่อนไปตามวันที่ระบุ`}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="p-3 bg-rose-50 border border-rose-100 rounded-xl text-[11px] font-bold text-rose-600 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                                        {error}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100 border border-slate-200 active:scale-95 cursor-pointer bg-white transition-all"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95 cursor-pointer transition-all border-none"
                                >
                                    บันทึก
                                </button>
                            </div>
                        </form>

                        {/* Interactive Live Preview (Right) */}
                        <div className="hidden lg:flex w-[400px] bg-slate-950 p-6 flex-col justify-between overflow-y-auto text-slate-100 min-h-[500px]">
                            <div>
                                <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-5">
                                    <div className="flex items-center gap-2">
                                        <Smartphone className="w-4 h-4 text-blue-400" />
                                        <span className="text-[11px] font-black tracking-wider text-slate-400">Live Device Preview</span>
                                    </div>

                                    {/* Preview Mode Switcher */}
                                    {newAnnouncement.show_popup && (
                                        <div className="flex bg-slate-900 border border-slate-800 p-0.5 rounded-lg text-[9px] font-black">
                                            <button
                                                onClick={() => setPreviewMode('post')}
                                                className={`px-2.5 py-1 rounded-md transition-all ${previewMode === 'post'
                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                    : 'text-slate-400 hover:text-white'
                                                    }`}
                                            >
                                                Post
                                            </button>
                                            <button
                                                onClick={() => setPreviewMode('popup')}
                                                className={`px-2.5 py-1 rounded-md transition-all ${previewMode === 'popup'
                                                    ? 'bg-blue-600 text-white shadow-sm'
                                                    : 'text-slate-400 hover:text-white'
                                                    }`}
                                            >
                                                Popup
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {previewMode === 'post' ? (
                                    /* Post mode render mockup */
                                    <div className="bg-slate-900 border border-slate-800/80 rounded-2xl overflow-hidden shadow-2xl p-4">
                                        <div className="text-[9px] font-black text-blue-400 mb-2 tracking-wider flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                                            Post View
                                        </div>

                                        {newAnnouncement.image_url && !imageErrors[newAnnouncement.image_url] ? (
                                            <div className="w-full rounded-xl overflow-hidden mb-3.5 bg-slate-800 flex justify-center items-center">
                                                <img
                                                    src={`${API_URL}${newAnnouncement.image_url}`}
                                                    alt="Live Banner Preview"
                                                    className="w-full h-auto max-h-48 object-contain"
                                                    onError={() => handleImageError(newAnnouncement.image_url)}
                                                />
                                            </div>
                                        ) : (
                                            <div className="h-32 rounded-xl mb-3.5 bg-slate-800/40 border border-slate-800/60 flex items-center justify-center overflow-hidden">
                                                <Megaphone className="w-12 h-12 text-slate-650/40 -rotate-12" />
                                            </div>
                                        )}

                                        <h3 className="text-xs font-black text-slate-100 line-clamp-2 leading-tight">
                                            {newAnnouncement.title || 'กรอกหัวข้อประกาศ...'}
                                        </h3>

                                        <p className="text-[10px] font-medium text-slate-400 mt-2 line-clamp-5 leading-relaxed whitespace-pre-wrap">
                                            {newAnnouncement.content || 'เนื้อหาประกาศประชาสัมพันธ์จะพรีวิวที่นี่แบบเรียลไทม์...'}
                                        </p>
                                    </div>
                                ) : (
                                    /* Popup mode render mockup */
                                    <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 relative flex items-center justify-center min-h-[300px]">
                                        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] rounded-2xl"></div>

                                        {/* Mock popup overlay */}
                                        <div className="bg-white text-slate-800 rounded-2xl w-full max-w-[280px] shadow-2xl z-10 border border-slate-100 animate-in zoom-in-95 duration-200 relative overflow-hidden flex flex-col">

                                            {/* Mock X close button at top right */}
                                            <div className="absolute top-3 right-3 z-20 p-1.5 bg-black/15 text-white rounded-full cursor-default backdrop-blur-sm shadow-sm">
                                                <X className="w-3 h-3" />
                                            </div>

                                            {/* Image banner */}
                                            {newAnnouncement.image_url && !imageErrors[newAnnouncement.image_url] ? (
                                                <div className="w-full bg-white relative flex justify-center items-center overflow-hidden max-h-[35vh] shrink-0">
                                                    <img
                                                        src={`${API_URL}${newAnnouncement.image_url}`}
                                                        alt="Mockup Popup"
                                                        className="w-full max-h-[35vh] object-contain"
                                                        onError={() => handleImageError(newAnnouncement.image_url)}
                                                    />
                                                </div>
                                            ) : null}

                                            <div className={`p-4 flex flex-col flex-1 min-h-0 ${(!newAnnouncement.image_url || imageErrors[newAnnouncement.image_url]) ? 'pt-8' : ''}`}>
                                                {(!newAnnouncement.image_url || imageErrors[newAnnouncement.image_url]) ? (
                                                    <div className="flex items-center gap-2 mb-2 pr-6">
                                                        <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-500 flex items-center justify-center border border-blue-100 shrink-0">
                                                            <Megaphone className="w-3 h-3 rotate-[-15deg]" />
                                                        </div>
                                                        <h4 className="text-[11px] font-black text-slate-800 leading-snug">
                                                            {newAnnouncement.title || 'กรอกหัวข้อประกาศ...'}
                                                        </h4>
                                                    </div>
                                                ) : (
                                                    <h4 className="text-[11px] font-black text-slate-800 leading-snug mb-1">
                                                        {newAnnouncement.title || 'กรอกหัวข้อประกาศ...'}
                                                    </h4>
                                                )}

                                                <p className={`text-[9px] font-semibold text-slate-500 leading-normal mt-1 whitespace-pre-wrap overflow-y-auto pr-1 mb-3 ${(!newAnnouncement.image_url || imageErrors[newAnnouncement.image_url]) ? 'flex-1 max-h-[45vh]' : 'max-h-24 shrink-0'}`}>
                                                    {newAnnouncement.content || 'กรอกเนื้อหาข่าวสาร...'}
                                                </p>

                                                {/* Mock Bottom Actions */}
                                                <div className="mt-auto shrink-0 flex flex-col pt-1">
                                                    {/* Mock Dots Indicator */}
                                                    <div className="flex justify-center items-center pt-2 pb-1 opacity-50">
                                                        <div className="flex gap-1">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-600 w-2.5" />
                                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                                                        </div>
                                                    </div>

                                                    {/* Snooze action link mock */}
                                                    {newAnnouncement.popup_snooze_days !== 0 && (
                                                        <div className="flex justify-center pt-2">
                                                            <span className="text-[9px] font-black text-slate-400 hover:text-black transition-all cursor-default text-center">
                                                                {newAnnouncement.popup_snooze_days === -1
                                                                    ? 'ไม่ต้องแสดงประกาศนี้อีก'
                                                                    : `ไม่ต้องแสดงอีก ${newAnnouncement.popup_snooze_days} วัน`
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Device mock footer info */}
                            <div className="text-[9.5px] font-bold text-slate-500 border-t border-slate-800 pt-4 flex items-center justify-between mt-6">
                                <span>เป้าหมาย: {
                                    newAnnouncement.target_group === 'All' ? 'ทุกคน' :
                                        (newAnnouncement.target_group === 'Resident' || newAnnouncement.target_group === 'User') ? 'ผู้พักอาศัย' :
                                            newAnnouncement.target_group === 'Guard' ? 'รปภ.' : 'เจ้าหน้าที่'
                                }</span>
                                <span>สถานะ: {newAnnouncement.is_active ? 'เผยแพร่' : 'ร่าง/ปิด'}</span>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* PREVIEW FULL SCREEN MODAL */}
            {previewAnnouncement && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
                        onClick={() => setPreviewAnnouncement(null)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-200 border border-slate-100 flex flex-col relative group max-h-[85vh]">

                        {/* Close button top right */}
                        <button
                            onClick={() => setPreviewAnnouncement(null)}
                            className="absolute top-4 right-4 z-30 p-1.5 bg-black/15 text-white hover:bg-black/30 rounded-full transition-all backdrop-blur-sm cursor-pointer border-none shadow-sm"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Image banner */}
                        {previewAnnouncement.image_url && !imageErrors[previewAnnouncement.image_url] && (
                            <div className="w-full bg-white relative flex justify-center items-center overflow-hidden max-h-[55vh] shrink-0">
                                <img
                                    src={`${API_URL}${previewAnnouncement.image_url}`}
                                    alt={previewAnnouncement.title}
                                    className="w-full max-h-[55vh] object-contain select-none"
                                    onError={() => handleImageError(previewAnnouncement.image_url)}
                                />
                            </div>
                        )}

                        <div className={`p-6 pb-8 flex-1 flex flex-col min-h-0 ${(!previewAnnouncement.image_url || imageErrors[previewAnnouncement.image_url]) ? 'pt-10' : ''}`}>

                            {/* Title Section with Inline Icon */}
                            {(!previewAnnouncement.image_url || imageErrors[previewAnnouncement.image_url]) ? (
                                <div className="flex items-center gap-2.5 mb-3 shrink-0 pr-8">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center border border-blue-100 shrink-0">
                                        <Megaphone className="w-4 h-4 rotate-[-15deg]" />
                                    </div>
                                    <h3 className="text-base font-black text-slate-800 leading-snug">{previewAnnouncement.title}</h3>
                                </div>
                            ) : (
                                <h3 className="text-base font-black text-slate-800 leading-snug mb-2 shrink-0">{previewAnnouncement.title}</h3>
                            )}

                            <p className="text-xs font-semibold text-slate-500 leading-relaxed whitespace-pre-wrap overflow-y-auto pr-1 mb-4 select-text flex-1 min-h-0">
                                {previewAnnouncement.content}
                            </p>

                            {/* Bottom Actions Area */}
                            <div className="mt-auto shrink-0 flex flex-col">
                                {/* Snooze Row */}
                                {previewAnnouncement.popup_snooze_days !== 0 && (
                                    <div className="flex justify-center pt-3">
                                        <button
                                            onClick={() => setPreviewAnnouncement(null)}
                                            className="py-1 text-[11px] font-black text-slate-400 hover:text-black transition-all cursor-pointer border-none bg-transparent text-center"
                                        >
                                            {previewAnnouncement.popup_snooze_days === -1
                                                ? 'ไม่ต้องแสดงประกาศนี้อีก'
                                                : `ไม่ต้องแสดงอีก ${previewAnnouncement.popup_snooze_days || 1} วัน`
                                            }
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* DELETE CONFIRM DIALOG */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[350] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-350"
                        onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
                            <p className="text-sm font-medium text-slate-400 mt-2">ข้อมูลนี้จะถูกลบถาวร ไม่สามารถกู้คืนได้</p>

                            <div className="flex gap-3 w-full mt-10">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 py-2 border border-slate-100 text-slate-500 rounded-2xl font-bold text-[14px] active:scale-95 hover:bg-slate-50"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 py-2 bg-rose-500 text-white rounded-2xl font-bold text-[14px] shadow-lg shadow-rose-100 active:scale-95 hover:bg-rose-600 uppercase tracking-wide"
                                >
                                    ลบข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SUCCESS POPUP */}
            {showSuccessPopup && (
                <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <Check className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{successMessage}</p>

                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-2.5 bg-blue-600 text-white rounded-2xl font-black text-[14px] tracking-wide active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-500/20"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ERROR POPUP */}
            {showErrorPopup && (
                <div className="fixed inset-0 z-[2010] flex items-center justify-center p-4 text-center">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowErrorPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6 shadow-inner">
                                <AlertCircle className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-rose-600 tracking-tight">เกิดข้อผิดพลาด</h3>
                            <p className="text-[13px] font-bold text-slate-400 mt-2 leading-relaxed">{errorMessage}</p>

                            <button
                                onClick={() => setShowErrorPopup(false)}
                                className="mt-8 w-full py-2.5 bg-slate-800 text-white rounded-2xl font-black text-[14px] tracking-wide active:scale-95 hover:bg-slate-900 shadow-lg shadow-slate-200"
                            >
                                เข้าใจแล้ว
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementManagement;
